import { ClickAwayListener, Paper, Popper } from '@mui/material';
import {
  DateCalendar,
  LocalizationProvider,
  PickersDayProps,
  StaticDatePicker,
} from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { PickerValue } from '@mui/x-date-pickers/internals';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { WeekPickerDay } from '@/components/WeekPickerDay';
import { ViewTable } from '@/configs/ViewTable';
import { DateNavigationModel } from '@/models/DateNavigationModel';

import { CalendarHeader } from './CalendarHeader';

const MONTH_NAMES: Record<string, string> = {
  'янв.': 'Янв.',
  'февр.': 'Февр.',
  'мар.': 'Март',
  'апр.': 'Апр.',
  мая: 'Май',
  'июн.': 'Июнь',
  'июл.': 'Июль',
  'авг.': 'Авг.',
  'сент.': 'Сент.',
  'окт.': 'Окт.',
  'нояб.': 'Нояб.',
  'дек.': 'Дек.',
};

const CustomMonthButton = React.forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & {
    value: number;
    children?: React.ReactNode;
    className?: string;
  }
>(({ children, className, ...props }, ref) => {
  const originalText = typeof children === 'string' ? children.trim() : '';
  const monthName = MONTH_NAMES[originalText] || originalText;
  const isSelected = className?.includes('Mui-selected');

  return (
    <Button
      {...props}
      ref={ref}
      color="primary"
      variant={isSelected ? 'contained' : 'text'}
      sx={{
        width: '100%',
        borderRadius: '20px',
        ...(!isSelected && {
          color: 'var(--primary-color)',
        }),
      }}
    >
      {monthName}
    </Button>
  );
});

CustomMonthButton.displayName = 'CustomMonthButton';

type CalendarProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  dateNavigation: DateNavigationModel;
};

const MIN_DATE = DateTime.fromObject({ year: 2020, month: 1, day: 1 });
const MAX_DATE = DateTime.fromObject({ year: 2030, month: 1, day: 1 });

const Calendar: React.FC<CalendarProps> = observer(({ anchorEl, onClose, dateNavigation }) => {
  const { view, currentDate, setCurrentDate } = dateNavigation;
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(currentDate);
  const [hoveredDay, setHoveredDay] = useState<DateTime | null>(null);
  const [calendarView, setCalendarView] = useState<'day' | 'month' | 'year'>('day');

  useEffect(() => {
    setSelectedDate(currentDate);
    setCalendarView('day'); // сбрасываем на день при открытии
  }, [currentDate, anchorEl]);

  const onPeriodChange = (newValue: PickerValue) => {
    if (newValue) {
      setSelectedDate(newValue);

      // Закрываем календарь и применяем дату только после выбора дня
      if (view === ViewTable.Week && calendarView === 'day') {
        setCurrentDate(newValue.startOf(view));
        onClose();
      } else if (view === ViewTable.Month) {
        // В режиме месяца применяем сразу
        setCurrentDate(newValue.startOf(view));
        onClose();
      }
    }
  };

  const handleViewChange = (newView: 'day' | 'month' | 'year') => {
    setCalendarView(newView);
  };

  if (!anchorEl) return null;

  return (
    <Popper open={!!anchorEl} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.15)',
            borderRadius: '12px',
            overflow: 'hidden',
            marginTop: '5px',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="ru">
            {view === ViewTable.Week ? (
              <DateCalendar
                sx={{
                  height: 'auto !important',
                  maxHeight: '400px',
                  overflow: 'auto',
                  '.MuiPickersSlideTransition-root': {
                    height: 'auto !important',
                    minHeight: '200px',
                  },
                  '.MuiMonthCalendar-root': {
                    padding: '0px 16px 16px 16px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    width: '100%',
                  },
                  '.MuiMonthCalendar-root > *': {
                    width: '100%',
                  },
                  '.MuiYearCalendar-root': {
                    padding: '0px 16px 16px 16px',
                    maxHeight: '300px',
                    overflow: 'auto',
                  },
                }}
                value={selectedDate}
                view={calendarView}
                onViewChange={handleViewChange}
                onChange={onPeriodChange}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                showDaysOutsideCurrentMonth
                views={['year', 'month', 'day']}
                slots={{
                  day: WeekPickerDay as unknown as React.ElementType<PickersDayProps>,
                  monthButton: CustomMonthButton,
                  calendarHeader: CalendarHeader as any,
                }}
                slotProps={{
                  day: (ownerState) =>
                    ({
                      selectedDay: selectedDate,
                      hoveredDay,
                      onPointerEnter: () => setHoveredDay(ownerState.day),
                      onPointerLeave: () => setHoveredDay(null),
                    }) as unknown as React.ComponentProps<typeof WeekPickerDay>,
                }}
              />
            ) : (
              <StaticDatePicker
                value={selectedDate}
                onChange={onPeriodChange}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                views={['month', 'year']}
                openTo="month"
                slots={{
                  monthButton: CustomMonthButton,
                }}
                slotProps={{
                  actionBar: {
                    actions: [],
                  },
                  toolbar: {
                    hidden: true,
                  },
                }}
                sx={{
                  '.MuiPickersLayout-contentWrapper': {
                    paddingBottom: 0,
                  },
                  '.MuiPickersLayout-root .MuiDateCalendar-root': {
                    height: 'auto !important',
                  },
                  '.MuiPickersLayout-root': {
                    height: 'fit-content',
                  },
                  '.MuiDateCalendar-root': {
                    height: 'auto',
                  },
                  '.MuiMonthCalendar-root': {
                    padding: '0px 16px 16px 16px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    width: '100%',
                  },
                  '.MuiMonthCalendar-root > *': {
                    width: '100%',
                  },
                  '.MuiMonthCalendar-root button.Mui-disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    '&:hover': {
                      transform: 'none',
                      boxShadow: 'none',
                    },
                  },
                }}
              />
            )}
          </LocalizationProvider>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
});

export default Calendar;
